import type { User } from '../types/auth';
import { AuthError, AuthErrorType } from '../errors/AuthError';
import { Platform } from '../platform';
import type { ApiResponse } from '../types';
import type { Session } from '@supabase/supabase-js';
import type { SupabaseAdapter } from '../interfaces/supabase';

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
}

interface RateLimitState {
  attempts: number;
  windowStart: number;
}

interface AuthErrorResponse {
  message: string;
  status?: number;
}

export class AuthService {
  private static adapter: SupabaseAdapter;
  private static rateLimits: Map<string, RateLimitState> = new Map();
  private static readonly DEFAULT_RATE_LIMIT: RateLimitConfig = {
    maxAttempts: 5,
    windowMs: 5 * 60 * 1000 // 5 minutes
  };

  static initialize(adapter: SupabaseAdapter) {
    this.adapter = adapter;
  }

  private static checkRateLimit(key: string): boolean {
    const now = Date.now();
    const state = this.rateLimits.get(key) || { attempts: 0, windowStart: now };

    if (now - state.windowStart >= this.DEFAULT_RATE_LIMIT.windowMs) {
      state.attempts = 0;
      state.windowStart = now;
    }

    if (state.attempts >= this.DEFAULT_RATE_LIMIT.maxAttempts) {
      throw AuthError.rateLimited();
    }

    state.attempts++;
    this.rateLimits.set(key, state);
    return true;
  }

  private static async validateSession(session: Session | null): Promise<boolean> {
    if (!session) return false;

    // Check if the session has actually expired
    const expiryTime = new Date(session.expires_at || '');
    const now = new Date();
    
    // Add a 5-minute buffer to prevent edge cases
    const bufferMs = 5 * 60 * 1000; // 5 minutes
    if (expiryTime.getTime() - now.getTime() <= bufferMs) {
      try {
        // Try to refresh the session before throwing an error
        const { data, error } = await this.adapter.refreshSession();
        if (error || !data.session) {
          await this.signOut();
          throw AuthError.sessionExpired();
        }
        return true;
      } catch {
        await this.signOut();
        throw AuthError.sessionExpired();
      }
    }

    return true;
  }

  private static async handleAuthError(error: AuthErrorResponse): Promise<never> {
    console.error('[Auth] Error:', error);
    
    // Check if the error is related to session expiry
    if (error.message?.toLowerCase().includes('expired')) {
      await this.signOut();
      throw AuthError.sessionExpired();
    }
    
    throw AuthError.fromError(error);
  }

  static async getCurrentSession(): Promise<Session | null> {
    try {
      const user = await this.adapter.getUser();
      if (!user) return null;
      
      // For now, we'll create a simple session object
      // In a real app, you'd want to handle this more robustly
      return {
        user,
        access_token: '',
        refresh_token: '',
      } as Session;
    } catch (error) {
      if (error instanceof AuthError && error.type === AuthErrorType.SESSION_EXPIRED) {
        return null;
      }
      return this.handleAuthError(error as AuthErrorResponse);
    }
  }

  static async getCurrentUser(): Promise<User | null> {
    try {
      return await this.adapter.getUser();
    } catch (error) {
      return this.handleAuthError(error as AuthErrorResponse);
    }
  }

  static async signIn(email: string, password: string): Promise<ApiResponse<User>> {
    try {
      if (!email || !this.isValidEmail(email)) {
        throw AuthError.invalidEmail();
      }

      this.checkRateLimit(`signin:${email}`);

      await this.adapter.signIn(email, password);
      const user = await this.adapter.getUser();

      return {
        data: user,
        error: null,
      };
    } catch (error) {
      const authError = AuthError.fromError(error as AuthErrorResponse);
      return {
        data: null,
        error: authError.message,
      };
    }
  }

  static async signUp(email: string, password: string): Promise<ApiResponse<User>> {
    try {
      if (!email || !this.isValidEmail(email)) {
        throw AuthError.invalidEmail();
      }

      if (!this.isPasswordStrong(password)) {
        throw AuthError.weakPassword();
      }

      this.checkRateLimit(`signup:${email}`);

      await this.adapter.signUp(email, password);
      const user = await this.adapter.getUser();

      return {
        data: user,
        error: null,
      };
    } catch (error) {
      const authError = AuthError.fromError(error as AuthErrorResponse);
      return {
        data: null,
        error: authError.message,
      };
    }
  }

  static async signOut(): Promise<void> {
    try {
      await this.adapter.signOut();
    } catch (error) {
      return this.handleAuthError(error as AuthErrorResponse);
    }
  }

  static async resetPassword(email: string): Promise<ApiResponse<null>> {
    try {
      if (!email || !this.isValidEmail(email)) {
        throw AuthError.invalidEmail();
      }

      this.checkRateLimit(`reset:${email}`);

      await this.adapter.resetPassword(email);

      return {
        data: null,
        error: null,
      };
    } catch (error) {
      const authError = AuthError.fromError(error as AuthErrorResponse);
      return {
        data: null,
        error: authError.message,
      };
    }
  }

  static async updatePassword(password: string): Promise<ApiResponse<User>> {
    try {
      await this.adapter.updatePassword(password);
      const user = await this.adapter.getUser();

      return {
        data: user,
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to update password',
      };
    }
  }

  static async signInWithProvider(provider: 'google' | 'github') {
    const { data, error } = await this.adapter.signInWithOAuth({
      provider,
      options: {
        redirectTo: Platform.select({
          web: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
          default: undefined
        }),
      },
    });
    if (error) throw error;
    return data;
  }

  static async signInWithGoogle(): Promise<void> {
    const { error } = await this.adapter.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: Platform.select({
          web: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
          default: 'myapp://auth/callback'
        }),
      },
    });

    if (error) throw new AuthError(error.message, error.status);
  }

  static async verifyEmail(token: string): Promise<{ user: User | null; session: Session | null }> {
    try {
      const { data, error } = await this.adapter.verifyOtp({
        token_hash: token,
        type: 'signup',
      });

      if (error) throw error;
      return data;
    } catch (error) {
      return this.handleAuthError(error as AuthErrorResponse);
    }
  }

  static async resendVerificationEmail(email: string): Promise<void> {
    try {
      if (!email || !this.isValidEmail(email)) {
        throw AuthError.invalidEmail();
      }

      this.checkRateLimit(`resend:${email}`);

      const { error } = await this.adapter.resend({
        type: 'signup',
        email,
      });

      if (error) throw error;
    } catch (error) {
      return this.handleAuthError(error as AuthErrorResponse);
    }
  }

  static async handleAuthCallback(code: string): Promise<void> {
    const { error } = await this.adapter.exchangeCodeForSession(code);
    if (error) throw new AuthError(error.message, error.status);
  }

  private static isPasswordStrong(password: string): boolean {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return (
      password.length >= minLength &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumbers &&
      hasSpecialChar
    );
  }

  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
} 